import React, { FC, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import BackdropProgressBar from "../../../../components/ui/BackdropProgressBar";
import useApi from "../../../../hooks/useApi";
import useContainerWidth from "../../../../hooks/useContainerWidth";
import { APPROVAL_ROLES, approvePifById, rejectPifById } from "../../../../services/pifService";
import { PifApprovalOutcome, PifApprovalRecord } from "../types";
import PifApprovalChainRail from "./PifApprovalChainRail";
import PifApprovalRecordPanel from "./PifApprovalRecordPanel";
import PifApprovalDecisionPanel from "./PifApprovalDecisionPanel";
import { formatExpiresIn } from "./approvalHelpers";
import "./PifApprovalReview.scss";

const WIDE_COL = 940;

// The approve/reject endpoints return only a result code (no server timestamp), so the done screen
// shows a local confirmation stamp — the authoritative signed time lives on the signature row.
const actionStamp = (): string =>
  new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

interface PifApprovalReviewProps {
  record: PifApprovalRecord;
  signerName: string;
  // Omitted on the emailed /sign/:token entry — those approvers have no list to return to.
  onBack?: () => void;
  onOutcome: (outcome: PifApprovalOutcome, pifId: string, signedAt: string) => void;
}

const PifApprovalReview: FC<PifApprovalReviewProps> = ({ record, signerName, onBack, onOutcome }) => {
  const roleMeta = APPROVAL_ROLES[record.currentRole];
  // `loading` covers the approve/reject POSTs below. Without it the screen sat inert while the
  // signature was being written — no spinner, and the decision buttons still live for a second
  // click. The backdrop is modal, so it doubles as the double-submit guard.
  const { loading, executeApi } = useApi();
  const [rootRef, width] = useContainerWidth<HTMLDivElement>();

  const wide = width >= WIDE_COL;
  const gridClass = `pif-approval-review__grid${wide ? " pif-approval-review__grid--wide" : ""}`;

  const chainDisplay = useMemo(
    () =>
      record.viewerIsCurrentSigner
        ? record.chain.map((c) => (c.state === "current" ? { ...c, name: signerName } : c))
        : record.chain,
    [record.chain, record.viewerIsCurrentSigner, signerName],
  );

  const handleSign = async () => {
    const result = await executeApi(approvePifById, record.postIfId);
    if (result) onOutcome("signed", record.pifId, actionStamp());
  };

  const handleSendBack = async (note: string) => {
    const result = await executeApi(rejectPifById, { postIfId: record.postIfId, reason: note, final: false });
    if (result) onOutcome("changes", record.pifId, actionStamp());
  };

  const handleDecline = async (note: string) => {
    const result = await executeApi(rejectPifById, { postIfId: record.postIfId, reason: note, final: true });
    if (result) onOutcome("declined", record.pifId, actionStamp());
  };

  return (
    <Box className="pif-approval-review" ref={rootRef}>
      <BackdropProgressBar loading={loading} />

      <Box className="pif-approval-review__topbar">
        <Box className="pif-approval-review__topbar-main">
          <Typography className="pif-approval-review__topbar-title">{record.pifId} · Post Interview Form</Typography>
          <Typography className="pif-approval-review__topbar-sub">{record.headerSub}</Typography>
        </Box>
        <Box className="pif-approval-review__topbar-chips">
          <Typography className="pif-approval-review__topbar-chip">
            {record.viewerIsCurrentSigner ? "Your signature is next" : "View only"}
          </Typography>
          {record.expiresAt && (
            <Typography className="pif-approval-review__topbar-chip pif-approval-review__topbar-chip--muted">
              {formatExpiresIn(record.expiresAt)}
            </Typography>
          )}
          {onBack && (
            <Button
              variant="outlined"
              size="small"
              onClick={onBack}
              className="pif-approval-review__back"
              data-testid="postif-approval-back"
            >
              Back to list
            </Button>
          )}
        </Box>
      </Box>

      <Box className={gridClass}>
        <PifApprovalChainRail chain={chainDisplay} audit={record.audit} />
        <PifApprovalRecordPanel record={record} />
      </Box>

      <Box className="pif-approval-review__footer">
        {record.viewerIsCurrentSigner ? (
          <PifApprovalDecisionPanel roleMeta={roleMeta} onSign={handleSign} onSendBack={handleSendBack} onDecline={handleDecline} />
        ) : (
          <Typography className="pif-approval-review__readonly-note">
            This record isn&apos;t waiting on your signature right now — it&apos;s shown here read-only.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PifApprovalReview;
